import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { AdminAccessesTab } from '../components/AdminAccessesTab';
import { AdminAudienceTab } from '../components/AdminAudienceTab';
import { AdminLiveTab } from '../components/AdminLiveTab';
import { AdminLoadTestTab } from '../components/AdminLoadTestTab';
import { AdminSecurityTab } from '../components/AdminSecurityTab';
import { AdminTrafficTab } from '../components/AdminTrafficTab';
import { LogoSeal } from '../components/Logo';
import { useAuth } from '../contexts/auth-context';
import type { AdminRange, AdminTraffic } from '../services/api';

/**
 * Painel de ADMINISTRAÇÃO da plataforma — visão de dono, não de criador.
 *
 * Responde a três perguntas, nesta ordem de urgência: está de pé agora (aba ao
 * vivo), como se comportou no período (tráfego), e quem está do outro lado
 * (público, acessos). A última aba existe para o teste de carga: mede o rastro
 * que ele deixou e apaga.
 *
 * O acesso é decidido pela API (ADMIN_EMAILS + middleware require-admin). O
 * `isAdmin` do usuário aqui só evita mostrar uma tela que daria 403.
 */

type Tab = 'live' | 'traffic' | 'audience' | 'accesses' | 'security' | 'loadtest';

const TABS: { id: Tab; label: string }[] = [
  { id: 'live', label: 'Ao vivo' },
  { id: 'traffic', label: 'Tráfego' },
  { id: 'audience', label: 'Público' },
  { id: 'accesses', label: 'Acessos' },
  { id: 'security', label: 'Segurança' },
  { id: 'loadtest', label: 'Teste de carga' },
];

const RANGES: { id: AdminRange; label: string }[] = [
  { id: '15m', label: '15 min' },
  { id: '1h', label: '1 hora' },
  { id: '6h', label: '6 horas' },
  { id: '24h', label: '24 horas' },
  { id: '7d', label: '7 dias' },
];

const TRAFFIC_FILTERS: { id: AdminTraffic; label: string }[] = [
  { id: 'all', label: 'Tudo' },
  { id: 'real', label: 'Só real' },
  { id: 'synthetic', label: 'Só teste' },
];

export function AdminPage() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState<Tab>('live');
  const [range, setRange] = useState<AdminRange>('24h');
  const [traffic, setTraffic] = useState<AdminTraffic>('all');

  // Precisa ser memoizado: as abas usam este objeto como dependência de
  // `useCallback`, e uma nova referência a cada render viraria um laço de
  // recarga infinito contra a API.
  const filters = useMemo(() => ({ range, traffic }), [range, traffic]);

  // A aba ao vivo mede a janela dela própria (segundos), e o recorte de
  // tráfego não se aplica ao rastro do teste — só as abas históricas filtram.
  const showsFilters =
    tab === 'traffic' || tab === 'audience' || tab === 'accesses' || tab === 'security';

  return (
    <div className="dashboard admin">
      <header className="dashboard__header">
        <div className="dashboard__brand">
          <LogoSeal size={32} />
        </div>
        <div className="dashboard__greeting">
          <h1 className="dashboard__title">Administração</h1>
          <p className="dashboard__subtitle">
            {user?.name} · <Link to="/dashboard">voltar para minha dashboard</Link>
          </p>
        </div>
        <button className="button button--ghost" type="button" onClick={logout}>
          Sair
        </button>
      </header>

      <nav className="dashboard__tabs" aria-label="Seções da administração">
        {TABS.map((item) => (
          <button
            key={item.id}
            className={`dashboard__tab${tab === item.id ? ' dashboard__tab--active' : ''}`}
            type="button"
            onClick={() => setTab(item.id)}
          >
            {item.label}
          </button>
        ))}
      </nav>

      {showsFilters && (
        <div className="admin__filters">
          <div className="filter-bar" role="group" aria-label="Período">
            {RANGES.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`filter-bar__option${
                  range === option.id ? ' filter-bar__option--active' : ''
                }`}
                onClick={() => setRange(option.id)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <div className="filter-bar" role="group" aria-label="Tipo de tráfego">
            {TRAFFIC_FILTERS.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`filter-bar__option${
                  traffic === option.id ? ' filter-bar__option--active' : ''
                }`}
                onClick={() => setTraffic(option.id)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <main className="dashboard__content">
        {tab === 'live' && <AdminLiveTab />}
        {tab === 'traffic' && <AdminTrafficTab filters={filters} />}
        {tab === 'audience' && <AdminAudienceTab filters={filters} />}
        {tab === 'accesses' && <AdminAccessesTab filters={filters} />}
        {tab === 'security' && <AdminSecurityTab filters={filters} />}
        {tab === 'loadtest' && <AdminLoadTestTab />}
      </main>
    </div>
  );
}
